import { useEffect, useState, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { getVersions, getVersionsForMajor, getLatestVersionForMajor, loadAllParasiteRateData, loadValidationFailures, type ParasiteRatePoint, type ValidationFailurePoint } from "@/services/parasiteRateService"
import ParasiteRateMap from "./ParasiteRateMap"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Progress } from "@/components/ui/progress"
import { URL_PARAM } from "./parasiteRateUrlState"

const PAGE_TITLE = "PR Clusters"

/**
 * Top-level interface component for the PR Clusters data page (the
 * cluster-point parasite rate map; one of the two Parasite Rate sub-pages).
 *
 * On mount, fetches the list of available dataset versions first, then loads
 * all parasite rate data for the latest version with a progress indicator
 * showing how many countries have been downloaded. Once fully loaded, renders
 * the deck.gl map. Validation failure data loads in the background after.
 *
 * @returns A React element filling the available page space with the map or a loader.
 */
export default function PrClustersInterface(): React.JSX.Element {
  const { getAccessToken } = useAuth()
  // Read-only access to the URL so a shared link can open the same dataset
  // version. The map component owns writing state back to the URL.
  const [searchParams] = useSearchParams()
  const [points, setPoints] = useState<ParasiteRatePoint[]>([])
  const [validationFailures, setValidationFailures] = useState<ValidationFailurePoint[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [loadProgress, setLoadProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 })
  const [dataVersion, setDataVersion] = useState<string>("")
  const [allVersions, setAllVersions] = useState<string[]>([])

  /**
   * Progress callback — tracks how many countries have finished downloading.
   * Only updates if progress moves forward (prevents backward jumps from
   * React Strict Mode double-mounting or concurrent invocations).
   */
  const handleProgress = useCallback((completed: number, total: number) => {
    setLoadProgress((prev) => {
      if (completed >= prev.completed) {
        return { completed, total }
      }
      return prev
    })
  }, [])

  useEffect(() => {
    async function fetchData(): Promise<void> {
      try {
        setIsLoading(true)
        setError(null)

        const token = await getAccessToken()

        // Step 1: Fetch all available versions. We ignore the backend's global
        // latest_version here because it may belong to a newer major the UI
        // does not support; the default is resolved within the supported major.
        const versionsResponse = await getVersions(token)
        const { versions } = versionsResponse
        setAllVersions(versions)

        const selectableVersions: string[] = getVersionsForMajor({ versions })
        const latestSupportedVersion: string | null = getLatestVersionForMajor({ versions })
        if (latestSupportedVersion == null) {
          throw new Error("No parasite rate dataset versions available for the supported major version")
        }

        // Prefer the version from a shared URL, but only when it is one of the
        // selectable versions (same set the dropdown shows); otherwise fall
        // back to the latest version within the supported major.
        const urlVersion: string | null = searchParams.get(URL_PARAM.version)
        const initialVersion: string = urlVersion != null && selectableVersions.includes(urlVersion)
          ? urlVersion
          : latestSupportedVersion
        setDataVersion(initialVersion)

        // Step 2: Load parasite rate data for the resolved version
        const data = await loadAllParasiteRateData(token, initialVersion, handleProgress)
        setPoints(data)

        // Step 3: Load validation failures in the background
        loadValidationFailures(token, initialVersion)
          .then((failures) => setValidationFailures(failures))
          .catch((err) => {
            console.error("PrClustersInterface: validation failures load failed:", err)
          })
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load parasite rate data"
        console.error("PrClustersInterface: data load failed:", err)
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    // searchParams is read once to seed the initial version; URL writes after
    // load must not re-run the full data load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken, handleProgress])

  /**
   * Handle version change from the VersionSelector. Reloads all data for the
   * newly selected version.
   *
   * @param version - The version string selected by the user.
   */
  const handleVersionChange = useCallback(async (version: string) => {
    try {
      setIsLoading(true)
      setError(null)
      setLoadProgress({ completed: 0, total: 0 })

      const token = await getAccessToken()
      setDataVersion(version)

      const result = await loadAllParasiteRateData(token, version, handleProgress)
      setPoints(result)

      // Reload validation failures for the new version in the background
      loadValidationFailures(token, version)
        .then((failures) => setValidationFailures(failures))
        .catch((err) => {
          console.error("PrClustersInterface: validation failures load failed:", err)
        })
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load parasite rate data"
      console.error("PrClustersInterface: version change failed:", err)
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAccessToken, handleProgress])

  if (isLoading) {
    const progressPercent = loadProgress.total > 0
      ? Math.round((loadProgress.completed / loadProgress.total) * 100)
      : 0

    return (
      <div className="flex flex-1 flex-col items-center justify-center min-h-0 w-full gap-4">
        <div className="shrink-0">
          <LoadingSpinner loadingContent="Loading parasite rate data..." size="default" />
        </div>
        <div className="w-64 flex flex-col items-center gap-2">
          <Progress value={progressPercent} className="h-2 w-full" />
          <p className="text-xs text-muted-foreground">
            {loadProgress.completed} / {loadProgress.total} countries
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center min-h-0 w-full">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    )
  }

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <div className="flex flex-1 flex-col min-h-0 w-full">
      <ParasiteRateMap
        points={points}
        validationFailures={validationFailures}
        currentVersion={dataVersion}
        allVersions={allVersions}
        onVersionChange={handleVersionChange}
        getAccessToken={getAccessToken}
      />
    </div>
    </>
  )
}
